<script setup lang="ts">
import type * as pf from '@amirada/lib-shared'
import { DataType, NodeType } from '@amirada/lib-shared'
import type * as tp from '@base/utils/types'
import type * as pfWilo from '@amirada/lib-wilo-shared'
import { PARAM } from '@amirada/lib-wilo-shared'
import { ut_wilo_getCurveId, ut_wilo_getExtraKeys, ut_wilo_isSalesPropHidden, ut_wilo_parseListValue } from '~/utils/utils'
import {
  ut_filterListValues,
  ut_getListValues,
  ut_getText,
  ut_isEmbedded,
  ut_isPrint,
  ut_returnNewRoute,
} from '@base/utils/utils'
import { useAppIndexStore } from '@base/stores/indexStore'
import { def_printCompareImageWidth, EPrintRes, ESpaixType } from '~/utils/constants'

const p = defineProps<{
  tableNode: pfWilo.TableNode | pf.TPfNode | undefined
  countryLanguage: string
  realCountry: string
  isInternal: boolean
  isHistoric?: boolean
  isReplacement?: boolean
  isCompare?: boolean
  isQuickSelect?: boolean
  isProduct?: boolean
  extraValues?: pfWilo.TExtraValue[]
  extExtraValuesAr?: Record<string, pfWilo.TExtraValue[]>
  isDetail?: boolean
  dutyPointIsSet?: boolean
  dutyPointInput?: pfWilo.IDutyPointInput
  startIndex?: number
  // How many products the WHOLE comparison has, when `tableNode` is only one print group of it.
  // Goes with `startIndex`, and for the same reason: a group cannot tell from itself where it sits.
  totalNodes?: number
  compactView?: boolean
  isOld?: boolean
  vsxService?: string
  sessionId?: string
  spaixIdsAr?: Record<string, string>
  noReplacePumps?: boolean
}>()

const emit = defineEmits<{
  'update:remove': [nodeName: string]
}>()

const indexStore = useAppIndexStore()

const linkPropsType: string[] = ['@id_product_long_text', '@id_product']
const linkPropsSeries: string[] = ['@id_series_text']
const linkProps: string[] = linkPropsType.concat(linkPropsSeries)
const useSpaix: boolean = p.isProduct || p.isReplacement || p.isQuickSelect
const extraValuesAr: Ref<Record<string, pfWilo.TExtraValue[]>> = ref(p.extExtraValuesAr || {})
const openStateAr: Ref<string[]> = !p.noReplacePumps ? ref(['c0', 'c1']) : ref(['c0', 'c1', 'c2'])
let rCounter: number = 0

/*
 * The size of the headings inside the table — the section rows and the product names above the
 * columns.
 *
 * Print takes the table itself down to `print:text-table-print` (0.6rem), and the headings follow
 * by the same step: `css-lg` prints at 0.9375rem, `css-base` at 0.75rem — the 80 % the body text
 * already moved, so the proportion between heading and value stays what it is on screen.
 *
 * ⚑ One class or the other, never both: `css-lg` carries its own `print:text-lg-print!`, so an
 * added print size would leave the winner to the order of the stylesheet.
 */
const headingClass = computed((): string => ut_isPrint() ? 'css-base' : 'css-lg')

onMounted(async () => {
  // Use for print compare extra values from URL
  if (ut_isPrint() && p.isCompare && indexStore.state.paramData[PARAM.extraValues] && p.tableNode && p.tableNode.nodes) {
    const valuesAr: any[] = JSON.parse(indexStore.state.paramData[PARAM.extraValues])
    // ⚑ Offset by startIndex, exactly like printIndex below: param5 is written per position in the
    // WHOLE list, while `tableNode` here is only this print group. Without it the second group
    // reads the values of the first back, under its own names.
    for (const [nIndex, node] of p.tableNode.nodes.entries()) {
      extraValuesAr.value[node.nodeName] = JSON.parse(valuesAr[(p.startIndex || 0) + nIndex])
    }
  }
})

// Instead of using directly the table it's modified to a flat 2-dimensional array, then it's easier to dynamically integrate extra values from VSX
const calcTable = computed((): any[][] => {
  const res: any[][] = []
  if (!(p.tableNode && p.tableNode.nodes && (p.tableNode.header || p.isDetail))) { return res }
  let row: any[]
  let extraKey: string
  let val: string
  let validRow: boolean
  const headers: pf.TPfNode[] = p.isDetail ? p.tableNode.nodes : p.tableNode.header ? p.tableNode.header : []
  let hCount: number = 0
  for (const header of headers) {
    if (!header.attrs) { header.attrs = {} }
    if (header.cod !== 'graphic_data' && !p.isDetail) {
      header.cod = 'c' + hCount
      if (hCount === headers.length - 2) {
        header.attrs['last'] = '1'
      }
      hCount++
      row = [{ ...header }]
      res.push(row)
    }
    for (const prop of header.props || []) {
      // Same sales filter as every other table. No `isOld` — the comparison decides that per pump,
      // in ut_wilo_parseListValue below, and one row here spans several of them.
      if (!ut_wilo_isSalesPropHidden(prop)) {
        validRow = false
        row = [{ ...prop }]
        extraKey = prop.attrs?.Software_supplier === 'VSX' && prop.attrs.select_4_formula ? prop.attrs.select_4_formula : ''

        // Detail page
        if (p.isDetail) {
          val = extraKey ? getVsxVal(extraKey, p.extraValues || []) : '' // Only VSX at the moment
          if (val) {
            row.push(val)
            res.push(row)
          }
        }

        // Compare, Replacement or Quickselect
        else {
          // VSX property
          if (extraKey) {
            for (const node of p.tableNode.nodes) {
              val = getVsxVal(extraKey, extraValuesAr.value[node.nodeName] || [])
              validRow = !!val
              row.push(val)
            }
          // Normal property
          }
          else {
            for (const [index, value] of (ut_filterListValues(prop, p.tableNode?.nodes[0]?.values).entries() || extraKey)) {
              if (index > 0) {
                if (validRow) {
                  pushCheckRow(res, row, extraKey)
                }
                row = ['']
              } // No property name after first value
              for (const [nIndex, node] of p.tableNode.nodes.entries()) {
                val = ''
                val = ut_wilo_parseListValue(prop, p.isDetail ? value.desc : ut_getListValues(prop, node.values, index), isOldPump(nIndex + 1))
                if (prop.dataType !== DataType.dt_MediaRef && prop.cod !== 'fitting_piece_SFE') {
                  const values: pf.TPfValue[] = ut_filterListValues(prop, node.values)
                  if (values && values[index]) {
                    if (value.meta?.differ) {
                      val = '<b>' + val + '</b>'
                    }
                  }
                }
                validRow = !!val
                row.push(val)
              }
            }
          }
          if (validRow) {
            pushCheckRow(res, row, extraKey)
          }
        }
      }
    }
  }

  // Delete empty header compounds
  if (!p.isDetail) {
    // Delete empty header compounds (first all at the end)
    for (let i = res.length - 1; i > 0; i--) {
      if (res[i]?.length === 1) {
        res.splice(i, 1)
      }
      else {
        break
      }
    }
    // Delete empty header compounds (if doubled inside)
    for (let i = res.length - 1; i > 0; i--) {
      if ((res[i]?.length === 1 && res[i - 1]?.length === 1)) {
        res.splice(i - 1, 1)
      }
    }
  }
  return res
})

// Instead of using directly the table it's modified to a flat 2-dimensional array, then it's easier to dynamically integrate extra values from VSX
const mobileRows = computed((): number[] => {
  const res: number[] = []
  for (let i = 0; i < calcTable.value.length - 1; i++) {
    if (calcTable.value[i]) {
      const row = calcTable.value[i]
      if (row) {
        const node: pf.TPfNode = row[0]
        if (node.cod === 'c1') {
          res.push(i)
        }
        else if (node.nodeType === NodeType.nt_Compound && res.length === 1) {
          res.push(i)
          break
        }
      }
    }
  }
  return res
})

function pushCheckRow(res: any[][], row: any[], extraKey: string) {
  // Check for differences in VSX properties
  if (extraKey) {
    const allEqual: boolean = row.every((val, i, arr) => val === arr[1] || i < 1)
    if (!allEqual) {
      row = row.map((val, i) => {
        return i > 0 ? '<b>' + (val ? val : '-') + '</b>' : val
      })
    }
  }
  res.push(row)
}

function getVsxVal(extraKey: string, extraValues: pfWilo.TExtraValue[]): string {
  // some properties only show if a dutypoint is set
  if (['{KL.P_SPEED;;;1;0}', '{kg.DP_FreqCtrl_CTRLCVINDP}'].includes(extraKey) && !p.dutyPointIsSet) { return '' }

  let res: string = ''
  const extraValue: pfWilo.TExtraValue[] = extraValues.filter(v => v.Key === extraKey)
  if (extraValue && extraValue[0]) {
    res = extraValue[0].Value
  }
  return res
}

function getLink(node: pf.TPfNode | undefined, sourceFeature: string): tp.IParamRouter | string {
  if (!node) { return '' }
  let path: string = node.meta?.slugPath || ''
  if (p.isProduct && linkPropsSeries.includes(sourceFeature)) {
    const pathAr: string[] = path.split('/')
    pathAr.pop()
    path = pathAr.join('/')
  }
  if (!ut_isEmbedded()) {
    return ut_returnNewRoute('/' + p.realCountry + '/' + indexStore.state.paramData.lcda + '/' + path, {}, '', { noTab: true })
  }
  else {
    // strip from path 'web-config'
    const pathAr: string[] = path.split('/')
    if (pathAr.length < 2) { return '' }
    pathAr.shift()
    path = pathAr.join('/')
    let productsPath: string = indexStore.state.paramData.embeddedInfo.embparam2 || '' // 'https://wilo.com/de/de/Katalog/'
    if (productsPath) {
      if (!productsPath.endsWith('/')) { productsPath += '/' }
      return productsPath + indexStore.state.paramData.lcda + '/' + path // ToDo add dutychart settings
    }
    else {
      return ''
    }
  }
}

function getFittingLink(slugPath: string): tp.IParamRouter | '' {
  return ut_returnNewRoute('/' + p.realCountry + '/' + indexStore.state.paramData.lcda + '/' + slugPath, {}, '', { noTab: true })
}

/*
 * Is this column the pump being replaced? It is the LAST of the whole comparison — so the question
 * has to be asked in absolute terms, `startIndex + col`, against the total.
 *
 * ⚑ `tableNode.nodes.length` alone answers it only while `tableNode` IS the whole table. In print
 * it is one group of three (`ut_wilo_compareTablePrintGroups`), so the third column of EVERY group
 * came out as the old pump: heading `label-existing-pump` instead of the product name, and the
 * photo greyed out. On screen it was right, which is why it stood for so long.
 */
function isOldPump(col: number): boolean {
  return p.isReplacement && ((p.startIndex || 0) + col) === (p.totalNodes ?? p.tableNode?.nodes?.length)
}

const getExtraKeys = computed((): string[] => {
  return useSpaix && p.tableNode && p.tableNode.header ? ut_wilo_getExtraKeys(p.tableNode.header) : []
})

function getRowNode(row: any[]): pf.TPfNode | undefined {
  return row[0] ? row[0] : undefined
}

function getNode(cIndex: number): pf.TPfNode | undefined {
  return p.tableNode && p.tableNode.nodes && p.tableNode.nodes[cIndex] ? p.tableNode.nodes[cIndex] : undefined
}

function getRowClass(node: pf.TPfNode, rIndex: number): string {
  let res: string = ''
  if (node.nodeType === NodeType.nt_Compound) {
    rCounter = 0
  }
  else if (rCounter !== undefined) {
    // No background
    if ((rIndex === 1 && !p.isDetail) || node.dataType === DataType.dt_MediaRef) {
      res = ''
    // Striped background
    }
    else {
      res = rCounter % 2 === 0 ? 'bg-gunmetal-5' : 'bg-gunmetal-4'
    }

    if (calcTable.value.length >= rIndex + 1) {
      const row = calcTable.value[rIndex + 1]
      if (row && row[0]) {
        const node2: pf.TPfNode = row[0]
        if (node2 && node2.nodeType !== NodeType.nt_Compound) {
          rCounter++
        }
      }
    }
  }
  res += getVisibility(rIndex)
  return res
}

function getVisibility(rIndex: number): string {
  let res: string = ''
  for (let i = rIndex; i >= 0; i--) {
    const row = calcTable.value[i]
    if (row && row[0]) {
      const node: pf.TPfNode = row[0]
      if (node && node.nodeType === NodeType.nt_Compound) {
        if (!openStateAr.value.includes(row[0].cod) && !ut_isPrint()) {
          if (i === rIndex) {
            res = ' closed'
          }
          else {
            res = ' hidden'
          }
        }
        else {
          if (i === rIndex) {
            res = ' open'
          }
        }
        break
      }
    }
  }
  return res
}

function isLastComp(row: any): boolean {
  return row.attrs && row.attrs['last']
}

function toggleAccordion(compName: string) {
  const index: number = openStateAr.value.indexOf(compName)
  if (index > -1) {
    openStateAr.value.splice(index, 1)
  }
  else {
    openStateAr.value.push(compName)
  }
}

function showPumpCurveIcons(rIndex: number): boolean {
  let res: boolean = false
  if (calcTable.value.length >= rIndex) {
    const row = calcTable.value[rIndex]
    if (row && row[0]) {
      const node: pf.TPfNode = row[0]
      if (node.cod === 'c1') {
        res = true
      }
    }
  }
  return res
}
</script>

<template>
  <!-- Table mobile -->
  <div v-if="compactView">
    <div v-if="tableNode && tableNode.nodes" class="grid grid-cols-1 lg:grid-cols-2 gap-x-16 gap-y-8">
      <div v-for="(node, cIndex) in tableNode.nodes" :key="cIndex">
        <!-- Headline and icons -->
        <div class="flex justify-between items-center">
          <div v-if="calcTable[1]" class="css-lg font-bold" v-html="!isOldPump(cIndex + 1) ? calcTable[1][cIndex + 1] : ut_getText('label-existing-pump')" />
          <div v-if="!isOldPump(cIndex + 1)" class="flex flex-wrap gap-x-2 items-center">
            <CartIcons
              :is-compare-table="true"
              :is-compare="isCompare"
              :is-list="true"
              :is-product="isProduct"
              :item="node"
              :name-path="node.meta.namePath"
              :real-country="realCountry"
            />
            <template v-if="isCompare">
              <div v-if="!ut_isEmbedded()" class="mx-2">|</div>
              <UTooltip :text="ut_getText('txt_remove-from-productcompare')">
                <UIcon name="i-wilo-trash-solid-full" class="size-5.5" @click="emit('update:remove', node.nodeName)" />
              </UTooltip>
            </template>
          </div>
        </div>
        <!-- Photo and pumpcurve -->
        <div class="grid grid-cols-1 sm:grid-cols-2 mt-4">
          <div v-if="calcTable[2]" class="flex h-[120px]">
            <div class="my-auto">
              <LazyImage
                :src="calcTable[2][cIndex + 1] !== '-' ? calcTable[2][cIndex + 1] : useAppConfig().altPic"
                :alt="getNode(cIndex - 1)?.desc || ''"
                :click-max="calcTable[2][cIndex + 1] !== '-'"
                :is-old="isOldPump(cIndex + 1)"
                :max-height="120"
                :wilo-size="2"
              />
            </div>
          </div>
          <div>
            <LazyPumpCurve
              v-if="useSpaix && ut_wilo_getCurveId(getNode(cIndex)?.attrs)"
              :node="getNode(cIndex)"
              :key-name="(isCompare ? ESpaixType.sp_compare : isQuickSelect ? ESpaixType.sp_quickselect : ESpaixType.sp_replacement) + getNode(cIndex + 1)?.nodeName || ''"
              :prod-desc="getNode(cIndex + 1)?.desc || ''"
              :country-language="countryLanguage"
              :real-country="realCountry"
              :is-internal="isInternal"
              :is-historic="isHistoric || false"
              :is-compare="true"
              :is-compare-mobile="true"
              :is-quick-select="isQuickSelect"
              :vsx-service="vsxService"
              :session-id="sessionId"
              :spaix-id="spaixIdsAr ? spaixIdsAr[getNode(cIndex)?.nodeName || ''] : ''"
              :extra-keys="getExtraKeys"
              :duty-point-input="dutyPointInput"
              :print-index="(startIndex || 0) + cIndex + 1"
              @update:extra-values="extraValuesAr[getNode(cIndex + 1)?.nodeName || ''] = $event"
            />
          </div>
        </div>
        <!-- Only first data compound -->
        <div class="overflow-auto mt-4">
          <table v-if="tableNode && tableNode.nodes && mobileRows && mobileRows[0] && mobileRows[1]" class="table-fixed w-full">
            <thead />
            <tbody>
              <template v-for="(row, rIndex) in calcTable" :key="rIndex">
                <tr v-if="rIndex > mobileRows[0] && rIndex < mobileRows[1] && !(row[cIndex+1] === '-' || row[cIndex+1] === '<b>-</b>') " class="bg-gunmetal-5 even:bg-gunmetal-4">
                  <!-- Property -->
                  <td class="text-left hyphens-auto p-4 border-2s">
                    <div v-html="getRowNode(row)?.desc" />
                  </td>

                  <!-- Values -->
                  <td class="text-left hyphens-auto p-4">

                    <!-- Series or product link -->
                    <NuxtLink v-if="linkProps.includes(getRowNode(row)?.attrs?.SourceFeature || '') && !isOldPump(cIndex + 1)" :to="getLink(getNode(cIndex), getRowNode(row)?.attrs?.SourceFeature || '')">
                      <div class="text-primary underline" v-html="row[cIndex+1]" />
                    </NuxtLink>

                    <!-- Fitting piece -->
                    <div v-else-if="row[cIndex+1].includes('slugPath')">
                      <b v-if="JSON.parse(row[cIndex+1]).amount" v-html="JSON.parse(row[cIndex+1]).amount + ' x '" />
                      <NuxtLink :to="getFittingLink(JSON.parse(row[cIndex+1]).slugPath)">
                        <b class="text-primary underline" v-html="JSON.parse(row[cIndex+1]).pid" />
                      </NuxtLink>
                      <b v-html="'<br>' + JSON.parse(row[cIndex+1]).text" />
                    </div>

                    <!-- Standard -->
                    <div v-else v-html="row[cIndex+1]" />
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>

  <!--
    Table desktop.

    Every print group but the first starts on a fresh page — `startIndex` already says which one
    this is, so the decision is made here instead of by the caller.

    ⚑ It has to be a class on this element, not a `:style` handed to the component: an attribute
    that falls through to the root survives on the CLIENT but comes out of the SSR render as
    `style=""` (Vue reports it as a hydration style mismatch, and a mismatch is not rectified).
    The pdf is printed from exactly that SSR output, so a page break passed that way never reaches
    the document — which is what `Cart.vue` was doing.
  -->
  <div v-else class="overflow-auto" :class="{ 'break-before-page': (startIndex || 0) > 0 }">
    <!--
      ⚑ `print:text-table-print` (0.6rem) is the size the other print tables already use — List.vue
      and TabAccessoriesServices.vue set it per row. This one kept the body's `text-base-print`
      (0.75rem) and was the widest table in the pdf because of it. Set once on the table so every
      row inherits it; the column headings keep their own `css-lg`.
    -->
    <table v-if="tableNode && tableNode.nodes" class="w-full print:text-table-print">
      <thead />
      <tbody>
        <template v-for="(row, rIndex) in calcTable" :key="rIndex">

          <!-- Pumpcurve and cart icons not in calcTable -->
          <template v-if="showPumpCurveIcons(rIndex)">
            <!-- Pumpcurve -->
            <tr v-if="useSpaix">
              <td />
              <td v-for="(node, cIndex) in tableNode.nodes" :key="node.nodeName" class="align-top px-4 print:px-3">
                <div :class="ut_isPrint() ? 'flex' : ''">
                  <div :class="ut_isPrint() ? 'mx-auto' : ''">
                    <LazyPumpCurve
                      v-if="ut_wilo_getCurveId(getNode(cIndex)?.attrs)"
                      :node="getNode(cIndex)"
                      :key-name="(isCompare ? ESpaixType.sp_compare : isQuickSelect ? ESpaixType.sp_quickselect : ESpaixType.sp_replacement) + getNode(cIndex)?.nodeName || ''"
                      :prod-desc="getNode(cIndex)?.desc || ''"
                      :country-language="countryLanguage"
                      :real-country="realCountry"
                      :is-internal="isInternal"
                      :is-historic="isHistoric || false"
                      :is-compare="true"
                      :is-quick-select="isQuickSelect"
                      :vsx-service="vsxService"
                      :session-id="sessionId"
                      :spaix-id="spaixIdsAr ? spaixIdsAr[getNode(cIndex)?.nodeName || ''] : ''"
                      :extra-keys="getExtraKeys"
                      :duty-point-input="dutyPointInput"
                      :print-index="(startIndex || 0) + cIndex"
                      @update:extra-values="extraValuesAr[getNode(cIndex)?.nodeName || ''] = $event"
                    />
                  </div>
                </div>
              </td>
            </tr>
            <!-- Cart icons -->
            <tr v-if="(isReplacement || isCompare || isQuickSelect) && !ut_isPrint()">
              <td />
              <td v-for="(node, cIndex) in tableNode.nodes" :key="node.nodeName" class="p-4">
                <div v-if="!isOldPump(cIndex + 1)" class="flex">
                  <div class="flex flex-wrap items-center gap-x-2 mx-auto">
                    <CartIcons
                      :is-compare-table="true"
                      :is-compare="isCompare"
                      :is-list="true"
                      :is-product="isProduct"
                      :item="node"
                      :name-path="node.meta.namePath"
                      :real-country="realCountry"
                    />
                    <template v-if="isCompare">
                      <div v-if="!ut_isEmbedded()" class="mx-2">|</div>
                      <UTooltip :text="ut_getText('txt_remove-from-productcompare')">
                        <UIcon name="i-wilo-trash-solid-full" class="size-5.5" @click="emit('update:remove', node.nodeName)" />
                      </UTooltip>
                    </template>
                  </div>
                </div>
              </td>
            </tr>
          </template>

          <!-- calcTable rows -->
          <!--
            A section heading must not be the last thing on a page. `#__nuxt tr` already carries
            `break-inside-avoid` (main.css), which keeps a row whole; `break-after-avoid` on the
            compound row additionally binds it to the row below, so the heading travels to the next
            page with its first values instead of sitting alone at the bottom.
          -->
          <tr
            v-if="(rIndex > 0 || isDetail || noReplacePumps) && !(noReplacePumps && rIndex === 1)"
            :class="[getRowClass(row[0], rIndex), getRowNode(row)?.nodeType === NodeType.nt_Compound ? 'break-after-avoid' : '']"
          >
            <template v-for="(cell, cIndex) in row" :key="'r' + rIndex + 'c'+ cIndex">

              <!-- Compound -->
              <td
                v-if="getRowNode(row)?.nodeType === NodeType.nt_Compound"
                colspan="100%"
                class="text-left hyphens-auto pt-12 pb-6 print:py-3 cursor-pointer"
                :class="getVisibility(rIndex) === ' closed' ? (isLastComp(row[0]) ? '' : 'border-b-2 border-b-gunmetal-4') : !isOld ? 'text-primary' : 'text-gunmetal'"
              >
                <div class="flex items-center" @click="toggleAccordion(row[0].cod)">
                  <div class="font-bold" :class="headingClass" v-html="getRowNode(row)?.desc" />
                  <UIcon v-if="!ut_isPrint()" name="i-wilo-chevron-down-solid-full" class="ml-auto shrink-0 size-5.5 duration-200 ms-auto" :class="getVisibility(rIndex) === ' open' ? 'rotate-180 transition-transform duration-200W' : ''" />
                </div>
              </td>

              <!-- Property -->
              <!--
                The print min-widths shrink with the font: they were cut for 0.75rem, and at
                0.6rem the same text needs 80 % of the room. Left as they were, they would stay
                the floor and the table would keep overflowing whatever the font does —
                30 + 3 x 45 = 660 px against the 643 px an A4 page offers between pdf.ts's margins.
              -->
              <td v-else-if="cIndex === 0" class="text-left hyphens-auto p-4 print:p-3 border-2s min-w-40 print:min-w-24">
                <div v-if="(rIndex !== 1 || isDetail) && getRowNode(row)?.dataType !== DataType.dt_MediaRef" v-html="getRowNode(row)?.desc" />
              </td>

              <!-- Values -->
              <template v-if="cIndex > 0">

                <!-- Headline -->
                <td v-if="rIndex ===1 && !isDetail" class="text-center hyphens-auto p-4 print:p-3">
                  <div class="font-bold" :class="headingClass" v-html="!isOldPump(cIndex) ? cell : ut_getText('label-existing-pump')" />
                </td>

                <!-- Image -->
                <td v-else-if="getRowNode(row)?.dataType === DataType.dt_MediaRef" class="px-4 print:px-3">
                  <div class="flex mx-auto">
                    <div class="mx-auto">
                      <LazyImage
                        :src="cell !== '-' ? cell : useAppConfig().altPic"
                        :alt="getNode(cIndex - 1)?.desc || ''"
                        :click-max="cell !== '-'"
                        :is-old="isOldPump(cIndex)"
                        :width="ut_isPrint() ? def_printCompareImageWidth : 0"
                        :wilo-size="2"
                        :wilo-size-print="indexStore.state.paramData.print === EPrintRes.pr_screen ? 2 : 5"
                      />
                    </div>
                  </div>
                </td>

                <!-- Normal table cell -->
                <td v-else class="text-left hyphens-auto p-4 print:p-3 min-w-60 print:min-w-36">

                  <!-- Link if is print and article number -->
                  <template v-if="ut_isPrint() && getRowNode(row)?.nodeName === '8ae2819d5b31c2ca015b3dc4ba4f1612'">
                    <a class="text-primary underline" :href="'https://wilo.com/quick/' + countryLanguage + '/articleNumber/' + cell" v-html="cell" />
                  </template>

                  <!-- Series or product link -->
                  <NuxtLink v-else-if="!ut_isPrint() && linkProps.includes(getRowNode(row)?.attrs?.SourceFeature || '') && !isOldPump(cIndex)" :to="getLink(getNode(cIndex -1), getRowNode(row)?.attrs?.SourceFeature || '')">
                    <div class="text-primary underline" v-html="cell" />
                  </NuxtLink>

                  <!-- Fitting piece -->
                  <div v-else-if="cell.includes('slugPath')">
                    <b v-if="JSON.parse(cell).amount" v-html="JSON.parse(cell).amount + ' x '" />
                    <NuxtLink :to="getFittingLink(JSON.parse(cell).slugPath)">
                      <b class="text-primary underline" v-html="JSON.parse(cell).pid" />
                    </NuxtLink>
                    <b v-html="'<br>' + JSON.parse(cell).text" />
                  </div>

                  <!-- Standard -->
                  <div v-else v-html="cell" />
                </td>

              </template>
            </template>

          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>
